import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { PageTitle } from '../CoachDashboard/styles';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { athleteAPI, trainingSessionAPI, authAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import LoadingSpinner from '../Common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import DashboardSettings from './DashboardSettings';
import { AiFillSetting } from 'react-icons/ai';
import { hasPlanOrHigher } from '../../utils/subscriptionUtils';
import { getAthleteColor } from '../../utils/athleteColors';

const Icon = ({ component: Component, ...props }: { component: any; [key: string]: any }) => {
  return <Component {...props} />;
};

interface TrainingSession {
  _id: string;
  trainingType: 'ski_jumping' | 'ground_training' | 'cross_country' | 'competition';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  hillSize?: string;
  athleteIds: Array<{ _id: string; firstName: string; lastName: string }>;
  jumps?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string };
    distance?: number;
    hillSize?: string;
    rating?: number;
  }>;
  competitionJumps?: Array<{
    athleteId: { _id: string; firstName: string; lastName: string };
    trialRoundDistance?: number;
    firstRoundDistance?: number;
    secondRoundDistance?: number;
    hillSize?: string;
    result?: number;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  width: 100%;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
  margin-top: -2rem;
  @media (max-width: 768px) {
    margin-top: -1.5rem;
    overflow-x: hidden;
  }
`;

const WelcomeMsg = styled.div`
  font-size: 1rem;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 2.5rem;
  text-align: left;
  max-width: 700px;
  color: var(--text-gray);

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 1.5rem;
    padding-right: 6rem;
    max-width: 100%;
    word-wrap: break-word;
  }
`;

const Sj = styled.span`
  color: var(--accent-orange);
  font-weight: 700;
`;

const Mate = styled.span`
  color: var(--text-dark);
  font-weight: 700;
`;

// Metrics Grid
const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 1400px;
  margin-bottom: 3rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const MetricCard = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.2s, transform 0.2s;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }
`;

const MetricLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  opacity: 0.7;
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 900;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const MetricDescription = styled.div`
  font-size: 0.85rem;
  color: var(--text-gray);
  opacity: 0.7;
  margin-top: 0.25rem;
`;

// Content Grid
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  width: 100%;
  max-width: 1400px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const Section = styled.div`
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 900;
  color: var(--accent-orange);
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  letter-spacing: -0.02em;
  position: relative;
  padding-left: 1rem;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 100%;
    background: var(--accent-orange);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
`;

const TopPerformersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const PerformerItem = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const PerformerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
`;

const PerformerName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const PerformerDetails = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-gray);
  opacity: 0.7;
  font-family: 'Poppins', sans-serif;
`;

const PerformerValue = styled.div`
  font-size: 1.1rem;
  font-weight: 900;
  color: var(--accent-orange);
  font-family: 'Poppins', sans-serif;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const RecentSessionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SessionItem = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 12px;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SessionDate = styled.div`
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-gray);
  font-family: 'Poppins', sans-serif;
`;

const SessionType = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--accent-orange);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.25rem 0.75rem;
  background: rgba(255, 150, 0, 0.1);
  border-radius: 8px;
`;

const SessionDetails = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-gray);
  opacity: 0.7;
  font-family: 'Poppins', sans-serif;
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-gray);
  opacity: 0.6;
  font-size: 0.9rem;
  font-weight: 600;
`;

const SettingsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  margin-top: 1.5rem;
  margin-bottom: -4.0rem;
  @media (max-width: 768px) {
    margin-top: 2.5rem;
  }
`;

const TopRightGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 1.25rem;
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const AddFabContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  @media (max-width: 768px) {
    display: none;
  }
`;

const AddFabButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--accent-orange);
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  transition: transform 0.2s ease, background 0.2s;
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3);
  &:hover {
    background: var(--accent-orange-dark);
    transform: scale(1.05);
  }
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

const AddLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const AddFabMenu = styled.div`
  position: absolute;
  top: 0;
  right: 100%;
  margin-right: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 180px;
  z-index: 99;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateX(4px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @media (max-width: 768px) {
    top: 100%;
    right: 0;
    left: auto;
    margin-right: 0;
    margin-top: 0.35rem;
    animation: fadeInMobile 0.2s ease;
  }
  @keyframes fadeInMobile {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const AddFabOption = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  background: var(--accent-orange);
  color: #fff;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: -0.02em;
  &:hover {
    background: var(--accent-orange-dark);
  }
`;

const SettingsButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: var(--accent-orange);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: all 0.2s;
  &:hover {
    color: var(--accent-orange-dark);
    transform: scale(1.1);
  }
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.1rem;
  }
`;

const ChooseMetricsLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: pre-line;
  text-align: center;
  line-height: 1.2;
`;

const MetricsButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const HelpButton = styled(Link)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: transparent;
  color: var(--accent-orange);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.2s;
  &:hover {
    color: var(--accent-orange-dark);
    transform: scale(1.1);
  }
  @media (max-width: 768px) {
    width: 35px;
    height: 35px;
    font-size: 1.2rem;
  }
`;

const HelpButtonColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const HelpLabel = styled.span`
  font-size: 0.65rem;
  font-weight: 600;
  color: var(--text-gray);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  white-space: nowrap;
`;

const GraphSection = styled.div`
  width: 100%;
  max-width: 1400px;
  background: var(--bg-sidebar);
  border-radius: 16px;
  padding: 1.5rem 1.5rem 1.5rem 0.5rem;
  margin-bottom: 3rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media (max-width: 768px) {
    margin-left: -1.25rem;
    width: calc(100% + 2.5rem);
    padding: 1rem 1rem 1rem 0;
    border-radius: 0;
  }
`;

const GraphTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 800;
  color: var(--text-gray);
  margin-bottom: 0.75rem;
  text-align: center;
`;

const GraphContainer = styled.div`
  width: 100%;
  height: 260px;
  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical line {
    stroke: rgba(0,0,0,0.06);
  }
  .recharts-text {
    font-size: 0.7rem;
    fill: var(--text-gray);
  }
  .recharts-tooltip-wrapper {
    outline: none;
  }
  .recharts-legend-item-text {
    font-size: 0.65rem;
    font-weight: 600;
  }
  @media (max-width: 768px) {
    .recharts-legend-wrapper {
      margin-top: 1.5rem;
      padding-top: 0.35rem;
    }
    .recharts-legend-item {
      margin-bottom: 0.15rem;
      margin-top: 0;
    }
  }
`;

const CoachHome: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [showSettings, setShowSettings] = useState(false);
  const [topMetrics, setTopMetrics] = useState<string[]>(
    user?.dashboardPreferences?.topMetrics || ['totalJumps', 'totalSessions', 'totalWins']
  );
  const [bottomSections, setBottomSections] = useState<string[]>(
    user?.dashboardPreferences?.bottomSections || ['topPerformersBestDistance', 'plannedTrainingSessions']
  );
  const [addFabExpanded, setAddFabExpanded] = useState(false);
  const addFabContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!addFabExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (addFabContainerRef.current && !addFabContainerRef.current.contains(e.target as Node)) {
        setAddFabExpanded(false);
      }
    };
    const handleScroll = () => setAddFabExpanded(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [addFabExpanded]);

  useEffect(() => {
    if (user?.dashboardPreferences) {
      const prefs = user.dashboardPreferences as { topMetrics?: string[]; bottomSections?: string[] };
      setTopMetrics(prefs.topMetrics || ['totalJumps', 'totalSessions', 'totalWins']);
      setBottomSections(prefs.bottomSections || ['topPerformersBestDistance', 'plannedTrainingSessions']);
    }
  }, [user]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [athletesData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for accurate dashboard data
          bananaCupAPI.getAll().catch(() => []), // Handle Banana Cup fetch errors gracefully
        ]);

        setAthletes(athletesData);
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse?.sessions || []);
        setSessions(sessionsData);
        setBananaCups(bananaCupsData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSaveSettings = async (newTopMetrics: string[], newBottomSections: string[]) => {
    try {
      await authAPI.updateProfile({
        dashboardPreferences: {
          topMetrics: newTopMetrics,
          bottomSections: newBottomSections,
        },
      });
      setTopMetrics(newTopMetrics);
      setBottomSections(newBottomSections);
      await refreshProfile();
    } catch (error) {
      console.error('Error saving dashboard settings:', error);
    }
  };

  // Helper function to get hill size from all possible sources
  const getHillSize = (
    jumpHillSize: string | undefined,
    sessionHillSize: string | undefined
  ): string => {
    // Check all possible sources, prioritizing jump-level data, then session-level
    // Handle both undefined and empty string cases
    if (jumpHillSize && jumpHillSize.trim() !== '') {
      return jumpHillSize;
    }
    if (sessionHillSize && sessionHillSize.trim() !== '') {
      return sessionHillSize;
    }
    return '';
  };

  // Calculate all metrics
  const calculateMetrics = () => {
    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (s: TrainingSession): boolean => {
      const hasJumps = !!(s.jumps && s.jumps.length > 0);
      const hasCompetitionJumps = !!(s.competitionJumps && s.competitionJumps.length > 0);
      const hasGroundTrainings = !!(s.groundTrainings && s.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(s.crossCountryTrainings && s.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };
    
    // Filter out pre-planned sessions (sessions with no actual data)
    const recordedSessions = sessions.filter(isRecordedSession);
    
    const trainingSessions = recordedSessions.filter(s => s.trainingType !== 'competition');
    const skiJumpingSessions = recordedSessions.filter(s => s.trainingType === 'ski_jumping');
    const groundTrainingSessions = recordedSessions.filter(s => s.trainingType === 'ground_training');
    const competitionSessions = recordedSessions.filter(s => s.trainingType === 'competition');

    // Calculate "this month" date range
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfMonthStr = startOfMonth.toISOString().split('T')[0];

    let totalJumps = 0;
    let totalWins = 0;
    let jumpsThisMonth = 0;
    let winsThisMonth = 0;
    const allDistances: number[] = [];
    const distancesThisMonth: number[] = [];
    let bestDistance = 0;
    let bestDistanceThisMonth = 0;
    let sessionsThisMonth = 0;
    let skiJumpingSessionsThisMonth = 0;
    let groundTrainingSessionsThisMonth = 0;
    let competitionSessionsThisMonth = 0;

    // Only process recorded sessions (exclude pre-planned)
    recordedSessions.forEach((session: TrainingSession) => {
      const isThisMonth = session.date >= startOfMonthStr;
      
      if (isThisMonth && session.trainingType !== 'competition') {
        sessionsThisMonth++;
      }
      if (isThisMonth && session.trainingType === 'ski_jumping') {
        skiJumpingSessionsThisMonth++;
      }
      if (isThisMonth && session.trainingType === 'ground_training') {
        groundTrainingSessionsThisMonth++;
      }
      if (isThisMonth && session.trainingType === 'competition') {
        competitionSessionsThisMonth++;
      }

      if (session.jumps) {
        totalJumps += session.jumps.length;
        if (isThisMonth) {
          jumpsThisMonth += session.jumps.length;
        }
        session.jumps.forEach((jump: { distance?: number }) => {
          if (jump.distance) {
            allDistances.push(jump.distance);
            if (isThisMonth) {
              distancesThisMonth.push(jump.distance);
            }
            if (jump.distance > bestDistance) {
              bestDistance = jump.distance;
            }
            if (isThisMonth && jump.distance > bestDistanceThisMonth) {
              bestDistanceThisMonth = jump.distance;
            }
          }
        });
      }

      if (session.competitionJumps) {
        totalJumps += session.competitionJumps.length * 2;
        if (isThisMonth) {
          jumpsThisMonth += session.competitionJumps.length * 2;
        }
        session.competitionJumps.forEach((compJump: { 
          trialRoundDistance?: number;
          firstRoundDistance?: number; 
          secondRoundDistance?: number;
          result?: number;
        }) => {
          // Check trial round
          if (compJump.trialRoundDistance) {
            allDistances.push(compJump.trialRoundDistance);
            if (isThisMonth) {
              distancesThisMonth.push(compJump.trialRoundDistance);
            }
            if (compJump.trialRoundDistance > bestDistance) {
              bestDistance = compJump.trialRoundDistance;
            }
            if (isThisMonth && compJump.trialRoundDistance > bestDistanceThisMonth) {
              bestDistanceThisMonth = compJump.trialRoundDistance;
            }
          }
          // Check first round
          if (compJump.firstRoundDistance) {
            allDistances.push(compJump.firstRoundDistance);
            if (isThisMonth) {
              distancesThisMonth.push(compJump.firstRoundDistance);
            }
            if (compJump.firstRoundDistance > bestDistance) {
              bestDistance = compJump.firstRoundDistance;
            }
            if (isThisMonth && compJump.firstRoundDistance > bestDistanceThisMonth) {
              bestDistanceThisMonth = compJump.firstRoundDistance;
            }
          }
          // Check second round
          if (compJump.secondRoundDistance) {
            allDistances.push(compJump.secondRoundDistance);
            if (isThisMonth) {
              distancesThisMonth.push(compJump.secondRoundDistance);
            }
            if (compJump.secondRoundDistance > bestDistance) {
              bestDistance = compJump.secondRoundDistance;
            }
            if (isThisMonth && compJump.secondRoundDistance > bestDistanceThisMonth) {
              bestDistanceThisMonth = compJump.secondRoundDistance;
            }
          }
          if (compJump.result === 1) {
            totalWins++;
            if (isThisMonth) {
              winsThisMonth++;
            }
          }
        });
      }
    });

    // Add Banana Cup competitions and jumps to statistics
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach((cup: any) => {
        if (cup.competitions && Array.isArray(cup.competitions)) {
          cup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
              const isThisMonth = competition.date && competition.date >= startOfMonthStr;
              
              // Count each jump's rounds
              competition.jumps.forEach((jump: any) => {
                let rounds = 0;
                if (jump.firstRoundDistance) {
                  rounds++;
                  allDistances.push(jump.firstRoundDistance);
                  if (isThisMonth) {
                    distancesThisMonth.push(jump.firstRoundDistance);
                    jumpsThisMonth++;
                  }
                  if (jump.firstRoundDistance > bestDistance) {
                    bestDistance = jump.firstRoundDistance;
                  }
                  if (isThisMonth && jump.firstRoundDistance > bestDistanceThisMonth) {
                    bestDistanceThisMonth = jump.firstRoundDistance;
                  }
                }
                if (jump.secondRoundDistance) {
                  rounds++;
                  allDistances.push(jump.secondRoundDistance);
                  if (isThisMonth) {
                    distancesThisMonth.push(jump.secondRoundDistance);
                    jumpsThisMonth++;
                  }
                  if (jump.secondRoundDistance > bestDistance) {
                    bestDistance = jump.secondRoundDistance;
                  }
                  if (isThisMonth && jump.secondRoundDistance > bestDistanceThisMonth) {
                    bestDistanceThisMonth = jump.secondRoundDistance;
                  }
                }
                if (jump.thirdRoundDistance) {
                  rounds++;
                  allDistances.push(jump.thirdRoundDistance);
                  if (isThisMonth) {
                    distancesThisMonth.push(jump.thirdRoundDistance);
                    jumpsThisMonth++;
                  }
                  if (jump.thirdRoundDistance > bestDistance) {
                    bestDistance = jump.thirdRoundDistance;
                  }
                  if (isThisMonth && jump.thirdRoundDistance > bestDistanceThisMonth) {
                    bestDistanceThisMonth = jump.thirdRoundDistance;
                  }
                }
                if (jump.fourthRoundDistance) {
                  rounds++;
                  allDistances.push(jump.fourthRoundDistance);
                  if (isThisMonth) {
                    distancesThisMonth.push(jump.fourthRoundDistance);
                    jumpsThisMonth++;
                  }
                  if (jump.fourthRoundDistance > bestDistance) {
                    bestDistance = jump.fourthRoundDistance;
                  }
                  if (isThisMonth && jump.fourthRoundDistance > bestDistanceThisMonth) {
                    bestDistanceThisMonth = jump.fourthRoundDistance;
                  }
                }
                if (jump.fifthRoundDistance) {
                  rounds++;
                  allDistances.push(jump.fifthRoundDistance);
                  if (isThisMonth) {
                    distancesThisMonth.push(jump.fifthRoundDistance);
                    jumpsThisMonth++;
                  }
                  if (jump.fifthRoundDistance > bestDistance) {
                    bestDistance = jump.fifthRoundDistance;
                  }
                  if (isThisMonth && jump.fifthRoundDistance > bestDistanceThisMonth) {
                    bestDistanceThisMonth = jump.fifthRoundDistance;
                  }
                }
                totalJumps += rounds;
              });
              
              // Count competition sessions
              if (isThisMonth) {
                competitionSessionsThisMonth++;
              }
            }
          });
        }
      });
    }

    const averageDistance = allDistances.length > 0
      ? Math.round((allDistances.reduce((a, b) => a + b, 0) / allDistances.length) * 10) / 10
      : 0;

    const averageDistanceThisMonth = distancesThisMonth.length > 0
      ? Math.round((distancesThisMonth.reduce((a, b) => a + b, 0) / distancesThisMonth.length) * 10) / 10
      : 0;

    return {
      totalSessions: trainingSessions.length,
      skiJumpingSessions: skiJumpingSessions.length,
      groundTrainingSessions: groundTrainingSessions.length,
      totalJumps,
      averageDistance,
      bestDistance,
      totalWins,
      competitionSessions: competitionSessions.length,
      // This month data
      sessionsThisMonth,
      skiJumpingSessionsThisMonth,
      groundTrainingSessionsThisMonth,
      jumpsThisMonth,
      averageDistanceThisMonth,
      bestDistanceThisMonth,
      winsThisMonth,
      competitionSessionsThisMonth,
    };
  };

  const metrics = calculateMetrics();

  // Get metric label and value
  const getMetricDisplay = (metricKey: string) => {
    const labels: Record<string, string> = {
      totalSessions: 'Number of Training Sessions',
      skiJumpingSessions: 'Number of Ski Jumping Training Sessions',
      groundTrainingSessions: 'Number of Ground Training Sessions',
      totalJumps: 'Total Jumps',
      averageDistance: 'Average Distance',
      bestDistance: 'Best Distance',
      totalWins: 'Total Wins as a Coach',
      competitionSessions: 'Number of Competition Sessions',
    };

    const values: Record<string, number> = {
      totalSessions: metrics.totalSessions,
      skiJumpingSessions: metrics.skiJumpingSessions,
      groundTrainingSessions: metrics.groundTrainingSessions,
      totalJumps: metrics.totalJumps,
      averageDistance: metrics.averageDistance,
      bestDistance: metrics.bestDistance,
      totalWins: metrics.totalWins,
      competitionSessions: metrics.competitionSessions,
    };

    const thisMonthValues: Record<string, number> = {
      totalSessions: metrics.sessionsThisMonth,
      skiJumpingSessions: metrics.skiJumpingSessionsThisMonth,
      groundTrainingSessions: metrics.groundTrainingSessionsThisMonth,
      totalJumps: metrics.jumpsThisMonth,
      averageDistance: metrics.averageDistanceThisMonth,
      bestDistance: metrics.bestDistanceThisMonth,
      totalWins: metrics.winsThisMonth,
      competitionSessions: metrics.competitionSessionsThisMonth,
    };

    return {
      label: labels[metricKey] || metricKey,
      value: values[metricKey] || 0,
      unit: metricKey === 'averageDistance' || metricKey === 'bestDistance' ? 'm' : '',
      thisMonth: thisMonthValues[metricKey] || 0,
    };
  };

  // Calculate average distance for each athlete
  const calculateAverageDistance = (athleteId: string): number => {
    const distances: number[] = [];
    sessions.forEach((session: TrainingSession) => {
      if (session.jumps) {
        session.jumps.forEach((jump: { athleteId?: { _id: string }; distance?: number }) => {
          if (jump.athleteId?._id === athleteId && jump.distance) {
            distances.push(jump.distance);
          }
        });
      }
      if (session.competitionJumps) {
        session.competitionJumps.forEach((compJump: { 
          athleteId?: { _id: string }; 
          trialRoundDistance?: number;
          firstRoundDistance?: number; 
          secondRoundDistance?: number;
        }) => {
          if (compJump.athleteId?._id === athleteId) {
            if (compJump.trialRoundDistance) distances.push(compJump.trialRoundDistance);
            if (compJump.firstRoundDistance) distances.push(compJump.firstRoundDistance);
            if (compJump.secondRoundDistance) distances.push(compJump.secondRoundDistance);
          }
        });
      }
    });
    
    // Add Banana Cup distances
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach((cup: any) => {
        if (cup.competitions && Array.isArray(cup.competitions)) {
          cup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
              competition.jumps.forEach((jump: any) => {
                const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                if (jumpAthleteId === athleteId) {
                  if (jump.firstRoundDistance) distances.push(jump.firstRoundDistance);
                  if (jump.secondRoundDistance) distances.push(jump.secondRoundDistance);
                  if (jump.thirdRoundDistance) distances.push(jump.thirdRoundDistance);
                  if (jump.fourthRoundDistance) distances.push(jump.fourthRoundDistance);
                  if (jump.fifthRoundDistance) distances.push(jump.fifthRoundDistance);
                }
              });
            }
          });
        }
      });
    }
    
    return distances.length > 0
      ? Math.round((distances.reduce((a, b) => a + b, 0) / distances.length) * 10) / 10
      : 0;
  };

  // Calculate jumps per athlete
  const calculateJumpsPerAthlete = (athleteId: string): number => {
    let count = 0;
    sessions.forEach((session: TrainingSession) => {
      if (session.jumps) {
        count += session.jumps.filter((j: { athleteId?: { _id: string } }) => 
          j.athleteId?._id === athleteId
        ).length;
      }
      if (session.competitionJumps) {
        const compJumps = session.competitionJumps.filter((j: { athleteId?: { _id: string } }) => 
          j.athleteId?._id === athleteId
        );
        count += compJumps.length * 2; // Competition jumps count as 2
      }
    });
    
    // Add Banana Cup jumps (count all rounds, up to 5 per jump)
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach((cup: any) => {
        if (cup.competitions && Array.isArray(cup.competitions)) {
          cup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
              competition.jumps.forEach((jump: any) => {
                const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                if (jumpAthleteId === athleteId) {
                  let rounds = 0;
                  if (jump.firstRoundDistance) rounds++;
                  if (jump.secondRoundDistance) rounds++;
                  if (jump.thirdRoundDistance) rounds++;
                  if (jump.fourthRoundDistance) rounds++;
                  if (jump.fifthRoundDistance) rounds++;
                  count += rounds;
                }
              });
            }
          });
        }
      });
    }
    
    return count;
  };

  // Calculate sessions per athlete
  const calculateSessionsPerAthlete = (athleteId: string): number => {
    let sessionCount = sessions.filter((session: TrainingSession) => 
      session.athleteIds.some((a: { _id: string }) => a._id === athleteId) &&
      session.trainingType !== 'competition'
    ).length;
    
    // Add Banana Cup competitions
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach((cup: any) => {
        if (cup.competitions && Array.isArray(cup.competitions)) {
          cup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
              const hasAthleteJump = competition.jumps.some((jump: any) => {
                const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                return jumpAthleteId === athleteId;
              });
              if (hasAthleteJump) {
                sessionCount++;
              }
            }
          });
        }
      });
    }
    
    return sessionCount;
  };

  // Calculate current form (average rating from last 2 weeks)
  const calculateCurrentForm = (athleteId: string): number => {
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    
    const ratings: number[] = [];
    sessions.forEach((session: TrainingSession) => {
      const sessionDate = new Date(session.date);
      if (sessionDate >= twoWeeksAgo) {
        if (session.jumps) {
          session.jumps.forEach((jump: { 
            athleteId?: { _id: string }; 
            rating?: number;
          }) => {
            if (jump.athleteId?._id === athleteId && jump.rating) {
              ratings.push(jump.rating);
            }
          });
        }
      }
    });
    
    return ratings.length > 0
      ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
      : 0;
  };

  // Calculate best distance for each athlete with session details
  const athletesWithBestDistance = athletes.map(athlete => {
    let bestDistance = 0;
    let bestDistanceDate = '';
    let bestDistanceLocation = '';
    let bestDistanceHillSize = '';

    // Check all sessions for this athlete's jumps
    sessions.forEach((session: TrainingSession) => {
      // Regular jumps
      if (session.jumps) {
        session.jumps.forEach((jump: { 
          athleteId?: { _id: string }; 
          distance?: number;
          hillSize?: string;
        }) => {
          if (jump.athleteId?._id === athlete._id && jump.distance && jump.distance > bestDistance) {
            bestDistance = jump.distance;
            bestDistanceDate = session.date;
            bestDistanceLocation = session.location;
            // Check all possible sources for hill size
            bestDistanceHillSize = getHillSize(jump.hillSize, session.hillSize);
          }
        });
      }

      // Competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach((compJump: { 
          athleteId?: { _id: string }; 
          trialRoundDistance?: number;
          firstRoundDistance?: number; 
          secondRoundDistance?: number;
          hillSize?: string;
        }) => {
          if (compJump.athleteId?._id === athlete._id) {
            // Check trial round
            if (compJump.trialRoundDistance && compJump.trialRoundDistance > bestDistance) {
              bestDistance = compJump.trialRoundDistance;
              bestDistanceDate = session.date;
              bestDistanceLocation = session.location;
              // Check all possible sources for hill size
              bestDistanceHillSize = getHillSize(compJump.hillSize, session.hillSize);
            }
            // Check first round
            if (compJump.firstRoundDistance && compJump.firstRoundDistance > bestDistance) {
              bestDistance = compJump.firstRoundDistance;
              bestDistanceDate = session.date;
              bestDistanceLocation = session.location;
              // Check all possible sources for hill size
              bestDistanceHillSize = getHillSize(compJump.hillSize, session.hillSize);
            }
            // Check second round
            if (compJump.secondRoundDistance && compJump.secondRoundDistance > bestDistance) {
              bestDistance = compJump.secondRoundDistance;
              bestDistanceDate = session.date;
              bestDistanceLocation = session.location;
              // Check all possible sources for hill size
              bestDistanceHillSize = getHillSize(compJump.hillSize, session.hillSize);
            }
          }
        });
      }
    });

    // Check Banana Cup jumps
    if (bananaCups && Array.isArray(bananaCups)) {
      bananaCups.forEach((cup: any) => {
        if (cup.competitions && Array.isArray(cup.competitions)) {
          cup.competitions.forEach((competition: any) => {
            if (competition.isCompleted && competition.jumps && Array.isArray(competition.jumps)) {
              competition.jumps.forEach((jump: any) => {
                const jumpAthleteId = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
                if (jumpAthleteId === athlete._id) {
                  // Check all rounds (up to 5 rounds)
                  const distances = [
                    jump.firstRoundDistance,
                    jump.secondRoundDistance,
                    jump.thirdRoundDistance,
                    jump.fourthRoundDistance,
                    jump.fifthRoundDistance
                  ].filter(d => d && d > 0);
                  
                  distances.forEach(distance => {
                    if (distance > bestDistance) {
                      bestDistance = distance;
                      bestDistanceDate = competition.date;
                      bestDistanceLocation = competition.location;
                      bestDistanceHillSize = competition.hillSize || '';
                    }
                  });
                }
              });
            }
          });
        }
      });
    }

    return {
      ...athlete,
      bestDistance,
      bestDistanceDate,
      bestDistanceLocation,
      bestDistanceHillSize,
      averageDistance: calculateAverageDistance(athlete._id),
      jumpsCount: calculateJumpsPerAthlete(athlete._id),
      sessionsCount: calculateSessionsPerAthlete(athlete._id),
      currentForm: calculateCurrentForm(athlete._id),
    };
  });

  // Get top performers by best distance
  const topPerformersByDistance = [...athletesWithBestDistance]
    .sort((a, b) => (b.bestDistance || 0) - (a.bestDistance || 0))
    .slice(0, 5)
    .filter(a => a.bestDistance && a.bestDistance > 0);

  // Get top performers by average distance
  const topPerformersByAverageDistance = [...athletesWithBestDistance]
    .sort((a, b) => (b.averageDistance || 0) - (a.averageDistance || 0))
    .slice(0, 5)
    .filter(a => a.averageDistance && a.averageDistance > 0);

  // Get planned training sessions (future sessions with no data)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const plannedTrainingSessions = [...sessions]
    .filter((session: TrainingSession) => {
      const sessionDate = new Date(session.date);
      sessionDate.setHours(0, 0, 0, 0);
      
      // Check if session is in the future
      const isFuture = sessionDate >= today;
      
      // Check if session has no data (no jumps, no competition jumps, no ground trainings)
      const hasNoData = 
        (!session.jumps || session.jumps.length === 0) &&
        (!session.competitionJumps || session.competitionJumps.length === 0);
      
      return isFuture && hasNoData && session.trainingType !== 'competition';
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  // Get planned competition sessions
  const plannedCompetitionSessions = [...sessions]
    .filter((session: TrainingSession) => {
      const sessionDate = new Date(session.date);
      sessionDate.setHours(0, 0, 0, 0);
      
      const isFuture = sessionDate >= today;
      const hasNoData = 
        (!session.competitionJumps || session.competitionJumps.length === 0);
      
      return isFuture && hasNoData && session.trainingType === 'competition';
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  // Get jumps per athlete (sorted)
  const jumpsPerAthlete = [...athletesWithBestDistance]
    .map(a => ({ ...a, jumpsCount: a.jumpsCount || 0 }))
    .sort((a, b) => (b.jumpsCount || 0) - (a.jumpsCount || 0))
    .slice(0, 5);

  // Get weight of athletes (sorted by most recent weight)
  const athletesWithWeight = athletes
    .filter(a => a.weightHistory && a.weightHistory.length > 0)
    .map(athlete => {
      const sortedWeights = [...(athlete.weightHistory || [])].sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      return {
        ...athlete,
        latestWeight: sortedWeights[0]?.weight || 0,
        weightDate: sortedWeights[0]?.date || '',
      };
    })
    .sort((a, b) => (b.latestWeight || 0) - (a.latestWeight || 0))
    .slice(0, 5);

  // Get sessions per athlete (sorted)
  const sessionsPerAthlete = [...athletesWithBestDistance]
    .map(a => ({ ...a, sessionsCount: a.sessionsCount || 0 }))
    .sort((a, b) => (b.sessionsCount || 0) - (a.sessionsCount || 0))
    .slice(0, 5);

  // Get current form (sorted)
  const currentForm = [...athletesWithBestDistance]
    .map(a => ({ ...a, currentForm: a.currentForm || 0 }))
    .sort((a, b) => (b.currentForm || 0) - (a.currentForm || 0))
    .slice(0, 5)
    .filter(a => a.currentForm > 0);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Rating/Form graph: average rating per athlete per week (last 10 weeks, including current week)
  const graphData = useMemo(() => {
    const getJumpAthleteId = (j: { athleteId?: { _id?: string } | string }) => {
      const id = typeof j.athleteId === 'object' ? j.athleteId?._id : j.athleteId;
      return id != null ? String(id) : '';
    };
    const toDateStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const getMonday = (d: Date) => {
      const m = new Date(d);
      const day = m.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      m.setDate(m.getDate() + diff);
      return m;
    };
    const now = new Date();
    const rows: Record<string, number | string | null>[] = [];
    for (let i = 9; i >= 0; i--) {
      const thisWeekMonday = getMonday(now);
      let weekStart: Date;
      let weekEnd: Date;
      if (i === 0) {
        weekStart = new Date(thisWeekMonday);
        weekEnd = new Date(now);
      } else {
        weekStart = new Date(thisWeekMonday);
        weekStart.setDate(weekStart.getDate() - 7 * i);
        weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
      }
      const weekStartStr = toDateStr(weekStart);
      const weekEndStr = toDateStr(weekEnd);
      const row: Record<string, number | string | null> = { week: i === 0 ? 'Now' : `W${10 - i}` };

      athletes.forEach((athlete: Athlete) => {
        const aid = String(athlete._id);
        let sum = 0;
        let count = 0;
        sessions.forEach((session: TrainingSession) => {
          const sessionDate = typeof session.date === 'string' ? session.date : (session as any).date?.split?.('T')?.[0];
          if (!sessionDate || sessionDate < weekStartStr || sessionDate > weekEndStr) return;
          if (session.jumps) {
            session.jumps.forEach((j: any) => {
              if (getJumpAthleteId(j) === aid && (j.rating != null || j.rating === 0)) {
                sum += Number(j.rating);
                count++;
              }
            });
          }
          if (session.competitionJumps) {
            session.competitionJumps.forEach((cj: any) => {
              if (getJumpAthleteId(cj) !== aid) return;
              const ratings = [cj.trialRoundRating, cj.firstRoundRating, cj.secondRoundRating, cj.rating].filter(
                (r): r is number => r != null && (typeof r === 'number' || !isNaN(Number(r)))
              );
              ratings.forEach(r => {
                sum += Number(r);
                count++;
              });
            });
          }
        });
        row[aid] = count > 0 ? Math.round((sum / count) * 10) / 10 : null;
      });
      rows.push(row);
    }
    return rows;
  }, [sessions, athletes]);

  const formatTrainingType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Subscription: show competition/equipment if any athlete has hs60+; show Banana Cup if any has hs90+
  const hasAnyPaidPlan = athletes.some(a => hasPlanOrHigher((a.subscription?.plan as string) || 'free', 'hs60'));
  const hasBananaCupAccess = athletes.some(a => hasPlanOrHigher((a.subscription?.plan as string) || 'free', 'hs90'));

  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Render bottom section based on type
  const renderBottomSection = (sectionType: string) => {
    switch (sectionType) {
      case 'topPerformersBestDistance':
        return (
          <Section>
            <SectionTitle>Top Performers - Best Distance</SectionTitle>
            {topPerformersByDistance.length > 0 ? (
              <TopPerformersList>
                {topPerformersByDistance.map((athlete) => {
                  const details = [
                    athlete.bestDistanceDate ? formatDate(athlete.bestDistanceDate) : '',
                    athlete.bestDistanceLocation || '',
                  ].filter(Boolean).join(' • ');

                  return (
                    <PerformerItem
                      key={athlete._id}
                      to={`/coach/athletes/${athlete._id}`}
                    >
                      <PerformerInfo>
                        <PerformerName>
                          {athlete.firstName} {athlete.lastName}
                        </PerformerName>
                        {details && (
                          <PerformerDetails>{details}</PerformerDetails>
                        )}
                      </PerformerInfo>
                      <PerformerValue>{athlete.bestDistance}m</PerformerValue>
                    </PerformerItem>
                  );
                })}
              </TopPerformersList>
            ) : (
              <EmptyState>No performance data yet</EmptyState>
            )}
          </Section>
        );

      case 'topPerformersAverageDistance':
        return (
          <Section>
            <SectionTitle>Top Performers - Average Distance</SectionTitle>
            {topPerformersByAverageDistance.length > 0 ? (
              <TopPerformersList>
                {topPerformersByAverageDistance.map((athlete) => (
                  <PerformerItem
                    key={athlete._id}
                    to={`/coach/athletes/${athlete._id}`}
                  >
                    <PerformerInfo>
                      <PerformerName>
                        {athlete.firstName} {athlete.lastName}
                      </PerformerName>
                    </PerformerInfo>
                    <PerformerValue>{athlete.averageDistance}m</PerformerValue>
                  </PerformerItem>
                ))}
              </TopPerformersList>
            ) : (
              <EmptyState>No performance data yet</EmptyState>
            )}
          </Section>
        );

      case 'plannedTrainingSessions':
        return (
          <Section>
            <SectionTitle>Planned Training Sessions</SectionTitle>
            {plannedTrainingSessions.length > 0 ? (
              <RecentSessionsList>
                {plannedTrainingSessions.map((session) => (
                  <SessionItem
                    key={session._id}
                    to="/coach/training-planner"
                  >
                    <SessionHeader>
                      <SessionDate>{formatDate(session.date)}</SessionDate>
                      <SessionType>{formatTrainingType(session.trainingType)}</SessionType>
                    </SessionHeader>
                    <SessionDetails>
                      {session.location} • {session.startTime} - {session.endTime}
                    </SessionDetails>
                  </SessionItem>
                ))}
              </RecentSessionsList>
            ) : (
              <EmptyState>No planned training sessions</EmptyState>
            )}
          </Section>
        );

      case 'plannedCompetitionSessions':
        return (
          <Section>
            <SectionTitle>Planned Competition Sessions</SectionTitle>
            {plannedCompetitionSessions.length > 0 ? (
              <RecentSessionsList>
                {plannedCompetitionSessions.map((session) => (
                  <SessionItem
                    key={session._id}
                    to="/coach/training-planner"
                  >
                    <SessionHeader>
                      <SessionDate>{formatDate(session.date)}</SessionDate>
                      <SessionType>Competition</SessionType>
                    </SessionHeader>
                    <SessionDetails>
                      {session.location} • {session.startTime} - {session.endTime}
                    </SessionDetails>
                  </SessionItem>
                ))}
              </RecentSessionsList>
            ) : (
              <EmptyState>No planned competition sessions</EmptyState>
            )}
          </Section>
        );

      case 'jumpsPerAthlete':
        return (
          <Section>
            <SectionTitle>Number of Jumps per Athlete</SectionTitle>
            {jumpsPerAthlete.length > 0 ? (
              <TopPerformersList>
                {jumpsPerAthlete.map((athlete) => (
                  <PerformerItem
                    key={athlete._id}
                    to={`/coach/athletes/${athlete._id}`}
                  >
                    <PerformerInfo>
                      <PerformerName>
                        {athlete.firstName} {athlete.lastName}
                      </PerformerName>
                    </PerformerInfo>
                    <PerformerValue>{athlete.jumpsCount}</PerformerValue>
                  </PerformerItem>
                ))}
              </TopPerformersList>
            ) : (
              <EmptyState>No jump data yet</EmptyState>
            )}
          </Section>
        );

      case 'weightOfAthletes':
        return (
          <Section>
            <SectionTitle>Weight of Athletes</SectionTitle>
            {athletesWithWeight.length > 0 ? (
              <TopPerformersList>
                {athletesWithWeight.map((athlete) => (
                  <PerformerItem
                    key={athlete._id}
                    to={`/coach/athletes/${athlete._id}`}
                  >
                    <PerformerInfo>
                      <PerformerName>
                        {athlete.firstName} {athlete.lastName}
                      </PerformerName>
                      {athlete.weightDate && (
                        <PerformerDetails>{formatDate(athlete.weightDate)}</PerformerDetails>
                      )}
                    </PerformerInfo>
                    <PerformerValue>{athlete.latestWeight} kg</PerformerValue>
                  </PerformerItem>
                ))}
              </TopPerformersList>
            ) : (
              <EmptyState>No weight data yet</EmptyState>
            )}
          </Section>
        );

      case 'sessionsPerAthlete':
        return (
          <Section>
            <SectionTitle>Number of Training Sessions per Athlete</SectionTitle>
            {sessionsPerAthlete.length > 0 ? (
              <TopPerformersList>
                {sessionsPerAthlete.map((athlete) => (
                  <PerformerItem
                    key={athlete._id}
                    to={`/coach/athletes/${athlete._id}`}
                  >
                    <PerformerInfo>
                      <PerformerName>
                        {athlete.firstName} {athlete.lastName}
                      </PerformerName>
                    </PerformerInfo>
                    <PerformerValue>{athlete.sessionsCount}</PerformerValue>
                  </PerformerItem>
                ))}
              </TopPerformersList>
            ) : (
              <EmptyState>No session data yet</EmptyState>
            )}
          </Section>
        );

      case 'currentForm':
        return (
          <Section>
            <SectionTitle>Current Form of Athletes (Last 2 weeks)</SectionTitle>
            {currentForm.length > 0 ? (
              <TopPerformersList>
                {currentForm.map((athlete) => (
                  <PerformerItem
                    key={athlete._id}
                    to={`/coach/athletes/${athlete._id}`}
                  >
                    <PerformerInfo>
                      <PerformerName>
                        {athlete.firstName} {athlete.lastName}
                      </PerformerName>
                      <PerformerDetails>Average rating from last 2 weeks</PerformerDetails>
                    </PerformerInfo>
                    <PerformerValue>{athlete.currentForm}/10</PerformerValue>
                  </PerformerItem>
                ))}
              </TopPerformersList>
            ) : (
              <EmptyState>No rating data from last 2 weeks</EmptyState>
            )}
          </Section>
        );

      default:
        return null;
    }
  };

  const handleAddOption = (action: 'athlete' | 'training' | 'planner' | 'competition' | 'equipment' | 'banana') => {
    setAddFabExpanded(false);
    if (action === 'athlete') {
      navigate('/coach/athletes', { state: { openAddAthlete: true } });
      return;
    }
    if (action === 'training') {
      navigate('/coach/training-sessions', { state: { openNewSessionForm: true } });
      return;
    }
    if (action === 'planner') {
      navigate('/coach/training-planner', { state: { openPlanNewSession: true } });
      return;
    }
    if (action === 'competition') {
      navigate('/coach/competition-sessions', { state: { openNewCompetitionForm: true } });
      return;
    }
    if (action === 'equipment') {
      navigate('/coach/equipment', { state: { openAddEquipment: true } });
      return;
    }
    if (action === 'banana') {
      navigate('/coach/banana-cup', { state: { openNewLeagueForm: true } });
    }
  };

  return (
    <Container>
      <SettingsWrapper>
        <TopRightGroup>
          <AddFabContainer ref={addFabContainerRef}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              <AddFabButton
                onClick={() => setAddFabExpanded(prev => !prev)}
                title={addFabExpanded ? 'Close menu' : 'Add new'}
                aria-label={addFabExpanded ? 'Close menu' : 'Add new'}
              >
                +
              </AddFabButton>
              <AddLabel>ADD</AddLabel>
            </div>
            {addFabExpanded && (
              <AddFabMenu>
                <AddFabOption onClick={() => handleAddOption('athlete')}>Add new athlete</AddFabOption>
                <AddFabOption onClick={() => handleAddOption('training')}>Add new training session</AddFabOption>
                {hasAnyPaidPlan && (
                  <>
                    <AddFabOption onClick={() => handleAddOption('competition')}>Add new competition session</AddFabOption>
                    <AddFabOption onClick={() => handleAddOption('equipment')}>Add new equipment</AddFabOption>
                  </>
                )}
                {hasBananaCupAccess && (
                  <>
                    <AddFabOption onClick={() => handleAddOption('planner')}>Plan new training session</AddFabOption>
                    <AddFabOption onClick={() => handleAddOption('banana')}>Create new Banana Cup</AddFabOption>
                  </>
                )}
              </AddFabMenu>
            )}
          </AddFabContainer>
          <MetricsButtonColumn>
            <SettingsButton 
              onClick={() => setShowSettings(true)}
              title="Dashboard Settings"
              aria-label="Dashboard Settings"
            >
              <Icon component={AiFillSetting} />
            </SettingsButton>
            <ChooseMetricsLabel>{'CHOOSE\nMETRICS'}</ChooseMetricsLabel>
          </MetricsButtonColumn>
          <HelpButtonColumn>
            <HelpButton to="/how-to-start" title="How to start" aria-label="How to start">
              ?
            </HelpButton>
            <HelpLabel>HELP</HelpLabel>
          </HelpButtonColumn>
        </TopRightGroup>
      </SettingsWrapper>
      <DashboardSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        topMetrics={topMetrics}
        bottomSections={bottomSections}
        onSave={handleSaveSettings}
      />
      <PageTitle>Home Dashboard</PageTitle>
      <WelcomeMsg>
        Here you can find basic data from you or your athletes.
      </WelcomeMsg>

      {/* Graph section - Rating/Form, last 10 weeks, one line per athlete */}
      <GraphSection>
        <GraphTitle>Form of your Athletes in Last 10 Weeks</GraphTitle>
        <GraphContainer>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={graphData} margin={{ top: 8, right: 8, left: 0, bottom: isMobile ? 40 : 4 }}>
              <XAxis dataKey="week" tick={{ fontSize: 11 }} axisLine={{ stroke: 'rgba(0,0,0,0.12)' }} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} domain={[0, 10]} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', fontSize: '0.8rem' }}
                formatter={(value: number, name: string) => {
                  const a = athletes.find(x => String(x._id) === name);
                  const label = a ? `${a.firstName} ${a.lastName}` : name;
                  return [value != null ? `${value}` : '—', label];
                }}
                labelFormatter={label => `Week ${label}`}
              />
              <Legend formatter={(value) => value} />
              {athletes.map((athlete, idx) => (
                <Line
                  key={athlete._id}
                  type="monotone"
                  dataKey={String(athlete._id)}
                  name={`${athlete.firstName} ${athlete.lastName}`}
                  stroke={getAthleteColor(athlete, idx)}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </GraphContainer>
      </GraphSection>

      {/* Metrics */}
      <MetricsGrid>
        {topMetrics.map((metricKey) => {
          const metric = getMetricDisplay(metricKey);
          return (
            <MetricCard key={metricKey}>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricValue>{metric.value}{metric.unit}</MetricValue>
              <MetricDescription>{metric.thisMonth}{metric.unit} this month</MetricDescription>
            </MetricCard>
          );
        })}
      </MetricsGrid>

      {/* Content Grid */}
      <ContentGrid>
        {bottomSections.map((sectionType) => (
          <React.Fragment key={sectionType}>
            {renderBottomSection(sectionType)}
          </React.Fragment>
        ))}
      </ContentGrid>

    </Container>
  );
};

export default CoachHome;
